import { TOOLS } from "@/lib/explore/constants"
import {
  Ruler,
  Square,
  Mountain,
  ClipboardCheck,
  HelpCircle,
  Maximize,
  Minimize,
  X,
  Building2,
  Ban,
  Box,
} from "lucide-react"
import type { ReactNode } from "react"
import { useState, useEffect } from "react"
import { usePage } from "@inertiajs/react"
import type { MapRef } from "react-map-gl/mapbox"
import type { SharedProps } from "@/types"

import { StudyPanel } from "./study-panel"
import type { UseConstructionStudyReturn } from "@/hooks/explore/use-construction-study"

const TOOL_ICONS: Record<string, ReactNode> = {
  distance: <Ruler className="w-5 h-5" />,
  surface: <Square className="w-5 h-5" />,
  altitude: <Mountain className="w-5 h-5" />,
  etude: <ClipboardCheck className="w-5 h-5" />,
}

interface ToolsBarProps {
  activeTool: string | null
  setActiveTool: (tool: string | null) => void
  isStudyMode: boolean
  setIsStudyMode: (mode: boolean) => void
  setIsToolOn: (on: boolean) => void
  onShortcutsOpen: () => void
  selectedParcelsCount: number
  study?: UseConstructionStudyReturn
  mapRef: React.RefObject<MapRef | null>
}

export const ToolsBar = ({
  activeTool,
  setActiveTool,
  isStudyMode,
  setIsStudyMode,
  setIsToolOn,
  onShortcutsOpen,
  selectedParcelsCount,
  study,
  mapRef,
}: ToolsBarProps) => {
  const { permissions } = usePage<SharedProps>().props
  const canStudy = permissions?.explore?.study_mode ?? false
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [bearing, setBearing] = useState(0)

  useEffect(() => {
    const map = mapRef.current?.getMap?.()
    if (map) {
      const handleRotate = () => setBearing(map.getBearing() ?? 0)
      map.on("rotate", handleRotate)
      handleRotate()
      return () => {
        map.off("rotate", handleRotate)
      }
    }

    // Map not ready yet -- retry until it is
    let cleanup: (() => void) | undefined
    const interval = setInterval(() => {
      const m = mapRef.current?.getMap?.()
      if (m) {
        clearInterval(interval)
        const handleRotate = () => setBearing(m.getBearing() ?? 0)
        m.on("rotate", handleRotate)
        handleRotate()
        cleanup = () => m.off("rotate", handleRotate)
      }
    }, 200)

    return () => {
      clearInterval(interval)
      cleanup?.()
    }
  }, [mapRef])

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(!!document.fullscreenElement)
      setTimeout(() => mapRef.current?.resize(), 100)
    }
    document.addEventListener("fullscreenchange", handler)
    return () => document.removeEventListener("fullscreenchange", handler)
  }, [mapRef])

  const toggleFullscreen = () => {
    const root = document.getElementById("explore-map-v2-root")
    if (!root) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      root.requestFullscreen()
    }
  }

  const handleToolClick = (toolId: string) => {
    if (toolId === "compass") {
      mapRef.current?.easeTo({ bearing: 0, pitch: 0 })
      return
    }
    if (toolId === "etude") {
      if (selectedParcelsCount === 0) return
      setIsStudyMode(!isStudyMode)
      return
    }
    if (activeTool === toolId) {
      setActiveTool(null)
      setIsToolOn(false)
    } else {
      setActiveTool(toolId)
      setIsToolOn(true)
    }
  }

  const etudeDisabled = selectedParcelsCount === 0

  return (
    <>
      {/* Study panel above toolbar */}
      {isStudyMode && study && <StudyPanel study={study} />}

      <div className="absolute bottom-2 left-2 z-10 flex items-center gap-2" data-tour-step="tools-bar">
        <div className="bg-white/95 backdrop-blur-sm map-panel-shadow rounded-xl flex items-center p-2 gap-0.5">
          {TOOLS.filter((t) => t.id !== "compass" && (t.id !== "etude" || canStudy)).map((tool) => {
            const isEtude = tool.id === "etude"
            const disabled = isEtude && etudeDisabled
            return (
              <button
                key={tool.id}
                title={tool.label}
                onClick={() => !disabled && handleToolClick(tool.id)}
                className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg transition-[transform,box-shadow] duration-100 active:translate-y-px ${
                  disabled
                    ? "opacity-40 cursor-not-allowed text-gray-400"
                    : activeTool === tool.id || (isEtude && isStudyMode)
                      ? "bg-primary text-white shadow-sm shadow-primary/30"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-800 cursor-pointer"
                }`}
              >
                {TOOL_ICONS[tool.id]}
                <span className="text-xs leading-tight mt-0.5 font-medium truncate max-w-full px-0.5">
                  {tool.label}
                </span>
              </button>
            )
          })}
          <div className="w-px h-6 bg-gray-200 mx-0.5" />
          <button
            title="Nord"
            onClick={() => handleToolClick("compass")}
            className="flex flex-col items-center justify-center w-12 h-12 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-[transform,box-shadow] duration-100 active:translate-y-px cursor-pointer"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              style={{ transform: `rotate(${-bearing}deg)`, transition: "transform 0.15s ease-out" }}
            >
              <polygon fill="#f44336" points="12,2 15,12 12,10 9,12" />
              <polygon fill="#9e9e9e" points="12,22 9,12 12,14 15,12" />
            </svg>
            <span className="text-xs leading-tight mt-0.5 font-medium truncate max-w-full px-0.5">Boussole</span>
          </button>
          <button
            title="Plein ecran"
            onClick={toggleFullscreen}
            className="flex flex-col items-center justify-center w-12 h-12 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-[transform,box-shadow] duration-100 active:translate-y-px cursor-pointer"
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            <span className="text-xs leading-tight mt-0.5 font-medium truncate max-w-full px-0.5">Ecran</span>
          </button>
          <button
            title="Raccourcis clavier"
            onClick={onShortcutsOpen}
            data-tour-step="help-button"
            className="flex flex-col items-center justify-center w-12 h-12 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-[transform,box-shadow] duration-100 active:translate-y-px cursor-pointer"
          >
            <HelpCircle className="w-5 h-5" />
            <span className="text-xs leading-tight mt-0.5 font-medium truncate max-w-full px-0.5">Aide</span>
          </button>
        </div>

        {/* Study tools group - shown right of toolbar when study mode active */}
        {isStudyMode && study && (
          <div className="bg-white/95 backdrop-blur-sm map-panel-shadow rounded-xl flex items-center p-1 gap-0.5">
            <button
              title="Polygone"
              onClick={() => study.setActiveStudyTool(study.activeStudyTool === "polygon" ? null : "polygon")}
              className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg transition-[transform,box-shadow] duration-100 active:translate-y-px ${
                study.activeStudyTool === "polygon"
                  ? "bg-primary text-white shadow-sm shadow-primary/30"
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-800 cursor-pointer"
              }`}
            >
              <Building2 className="w-5 h-5" />
              <span className="text-xs leading-tight mt-0.5 font-medium truncate max-w-full px-0.5">Poly</span>
            </button>
            <button
              title="Zone interdite"
              onClick={() => study.setActiveStudyTool(study.activeStudyTool === "forbidden" ? null : "forbidden")}
              className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg transition-[transform,box-shadow] duration-100 active:translate-y-px ${
                study.activeStudyTool === "forbidden"
                  ? "bg-error text-white shadow-sm shadow-error/30"
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-800 cursor-pointer"
              }`}
            >
              <Ban className="w-5 h-5" />
              <span className="text-xs leading-tight mt-0.5 font-medium truncate max-w-full px-0.5">Interdit</span>
            </button>
            <button
              title="2D/3D"
              onClick={() => study.setDisplayMode(study.displayMode === "2D" ? "3D" : "2D")}
              className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg transition-[transform,box-shadow] duration-100 active:translate-y-px ${
                study.displayMode === "3D"
                  ? "bg-primary text-white shadow-sm shadow-primary/30"
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-800 cursor-pointer"
              }`}
            >
              <Box className="w-5 h-5" />
              <span className="text-xs leading-tight mt-0.5 font-medium truncate max-w-full px-0.5">
                {study.displayMode}
              </span>
            </button>
          </div>
        )}
      </div>

      {activeTool && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 z-20 anim-slide-up pointer-events-none">
          <div className="pointer-events-auto bg-gray-900/90 backdrop-blur-sm text-white text-xs font-medium pl-3 pr-1.5 py-2 rounded-xl shadow-lg flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            {TOOLS.find((t) => t.id === activeTool)?.label} actif {"\u2014"} Cliquez sur la carte
            <button
              onClick={() => {
                setActiveTool(null)
                setIsToolOn(false)
              }}
              className="p-0.5 rounded-md hover:bg-white/15 transition-colors ml-1"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  )
}
